import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { LayoutSlotContainerRenderer } from '#pplx/frontend/framework/templates/LayoutSlotContainerRenderer.js';
import { slotTokenTemplateNames } from '#pplx/frontend/framework/templates/template.js';
import { useTrackRender } from '#pplx/frontend/framework/templates/TemplateDebugContext.js';
import { templateEngineStore } from '#pplx/frontend/framework/templates/templateEngineStore.js';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from 'zustand';
export class TemplateBuilder {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous slot types
    slotContainerComponents = new Map();
    templateName;
    constructor(name) {
        this.templateName = name;
    }
    // K deliberately has no default: `.slot<Props>('name', …)` (partial type
    // application) would disable inference and widen K to string, silently
    // degrading slot-name checking. Always pass both: `.slot<Props, 'name'>`.
    slot(name, SlotContainerComponent) {
        this.slotContainerComponents.set(name, SlotContainerComponent);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this;
    }
    /**
     * Finalize the template definition with a layout render function.
     *
     * Returns `{ Renderer, Slot, useSlotProps }`:
     * - Renderer renders the layout with portal containers
     * - Slot portals children into those containers via createPortal
     * - useSlotProps returns a type-safe setter for portal container props
     *
     * They communicate through a Zustand store, so they can be rendered as
     * independent siblings anywhere in the tree.
     */
    define(Layout) {
        const templateName = this.templateName;
        const slotContainerComponents = this.slotContainerComponents;
        function Renderer(props) {
            useTrackRender(`Renderer:${templateName}`);
            const SlotWithStore = useMemo(() => {
                const SlotFn = ({ slot }) => {
                    const SlotContainerComponent = slotContainerComponents.get(slot);
                    // eslint-disable-next-line react/jsx-no-useless-fragment -- return type must be ReactElement
                    if (!SlotContainerComponent)
                        return _jsx(_Fragment, {});
                    return (_jsx(LayoutSlotContainerRenderer, { templateName: templateName, slotName: slot, SlotContainerComponent: SlotContainerComponent }));
                };
                slotTokenTemplateNames.set(SlotFn, templateName);
                return SlotFn;
            }, []);
            return _jsx(Layout, { Slot: SlotWithStore, props: props });
        }
        function BoundSlot(props) {
            useTrackRender(`BoundSlot:${templateName}`);
            const { slot, children } = props;
            const slotName = slot;
            const [isMounted, setIsMounted] = useState(false);
            useLayoutEffect(() => {
                // A replacement renderer registers its containers during commit. Wait for that
                // before attaching children so they do not mount into the previous page's container.
                setIsMounted(true);
                templateEngineStore.getState().setSlotActive(templateName, slotName, true);
                return () => {
                    templateEngineStore.getState().setSlotActive(templateName, slotName, false);
                };
            }, [slotName]);
            const portalContainer = useStore(templateEngineStore, (s) => {
                return s.templates.get(templateName)?.containers.get(slotName) ?? null;
            });
            if (!isMounted || !portalContainer)
                return null;
            return createPortal(children, portalContainer);
        }
        function useSlotProps() {
            const set = useCallback((props) => {
                templateEngineStore
                    .getState()
                    .setSlotProps(templateName, props);
            }, []);
            return { set };
        }
        Renderer.displayName = `${templateName}.Renderer`;
        BoundSlot.displayName = `${templateName}.Slot`;
        slotTokenTemplateNames.set(BoundSlot, templateName);
        return {
            Renderer,
            Slot: BoundSlot,
            useSlotProps,
        };
    }
}
//# sourceMappingURL=templateComponents.js.map